import styled, { css, keyframes } from 'styled-components';

// Animation pour une apparition en douceur
const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const scaleIn = keyframes`
    from {
        transform: scale(0.9);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
`;

// L'overlay sombre qui couvre toute la page
export const PopupOverlay = styled.div`
    position: fixed; /* Reste en place même si on scrolle */
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6); /* Fond noir semi-transparent */
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1; /* S'assure qu'il est au-dessus de tout le reste */
    animation: ${fadeIn} 0.3s ease-out;
`;

// Le conteneur de la popup (le menu blanc au milieu)
export const PopupContainer = styled.div`
    background-color: ${(props) => props.theme.surface};
    padding: 2rem;
    border-radius: ${(props) => props.theme.radius.md};
    box-shadow: ${(props) => props.theme.shadowMd};
    z-index: 2;
    min-width: 700px; /* Largeur minimale */
    max-width: 50%; /* Largeur maximale */
    position: relative; /* Nécessaire pour positionner le bouton de fermeture */
    animation: ${scaleIn} 0.3s ease-out;
`;

export const PopupHeader = styled.div`
    border-bottom: 1px solid ${(props) => props.theme.borderStrong};
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    h2 {
        margin: 0;
        font-size: 1.5rem;
        color: inherit;
    }
`;

export const PopupBody = styled.div`
    margin-bottom: 1.5rem;
    color: inherit;
`;

export const PopupFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
`;

export const Button = styled.button`
    padding: 0.75rem 1.5rem;
    border-radius: ${(props) => props.theme.radius.sm};
    border: none;
    cursor: pointer;
    font-weight: bold;
    transition: all 0.2s ease;

    /* Style pour le bouton principal */
    &.primary {
        background-color: ${(props) => props.theme.accent};
        color: ${(props) => props.theme.onAccent};
        &:hover {
            background-color: ${(props) => props.theme.accentHover};
        }
    }

    /* Style pour le bouton secondaire (Annuler) */
    &.secondary {
        background-color: ${(props) => props.theme.surface2};
        color: inherit;
        &:hover {
            background-color: ${(props) => props.theme.surface3};
        }
    }
`;

export const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: inherit;
    &:hover {
        color: ${(props) => props.theme.accentText};
    }
`;

const keyFramesSpinner = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`

export const DivLoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    &.loader-container {
        width: 100%;
    }
`

export const DivSpinner = styled.div`
    &.spinner {
        width: 64px;
        height: 64px;
        border: 8px solid;
        border-color: ${(props) => props.theme.borderStrong} transparent ${(props) => props.theme.borderStrong} transparent;
        border-radius: 50%;
        animation: spin-anim 1.2s linear infinite;
        animation-name: ${keyFramesSpinner};
    }
`

export const PSelectedRow = styled.p `
    font-size: 0.9rem;
    margin: 0.3rem 0;
    display: flex;
    align-items: center;
    gap: 0.2rem;
    img {
        height: 20px;
        width: 20px;
        flex-shrink: 0;
    }
`

export const DivSummaryResult = styled.div `
    background-color: ${(props) => props.theme.surface2};
    border-radius: ${(props) => props.theme.radius.sm};
    border: 1px solid ${(props) => props.theme.borderStrong};
    padding: 0.5rem 1rem 1rem 1rem;
    margin-top: 1.5rem;
    h5 {
        margin: 0;
    }
    i {
        font-size: 1.2rem;
        transition: transform 0.2s ease-in-out;
    }
`;

export const SummaryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 0.5rem 0;
`;

export const SpanResultLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    margin: 8px 0;
    border-radius: ${props => props.theme.radius.sm};
    background-color: ${props => props.theme.surface};
    border-left: 3px solid transparent;
    transition: all 0.2s ease;
    
    ${props => props.status === 'success' && `
        border-left-color: ${props.theme.ok};
        background-color: ${props.theme.okSoft};
    `}

    ${props => props.status === 'success' && props.verified === false && 
    css`
        border-left-color: ${props.theme.warn};
        background-color: ${props.theme.warnSoft};
    `}
    
    ${props => props.status === 'failed' && `
        border-left-color: ${props.theme.danger};
        background-color: ${props.theme.dangerSoft};
    `}
    
    code {
        background-color: ${props => props.theme.surface2};
        padding: 2px 6px;
        border-radius: ${props => props.theme.radius.sm};
        font-size: 12px;
        font-family: 'Courier New', monospace;
    }
`;

export const UserInfoColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

export const ErrorMessage = styled.span`
    font-size: 13px;
    color: ${props => props.theme.danger};
    font-style: italic;
`;

export const CommentList = styled.ul`
    margin: 4px 0 0 0;
    padding-left: 20px;
    font-size: 12px;
    color: ${props => props.theme.text2};
    li {
        margin: 2px 0;
    }
`;

export const StatusBadge = styled.span`
    font-size: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
`;

export const DivResultContent = styled.div`
    max-height: ${props => props.visible ? '400px' : '0'};
    overflow: ${props => props.visible ? 'auto' : 'hidden'};
    transition: max-height 0.3s ease;
    padding: ${props => props.visible ? '0 1rem 1rem 1rem' : '0'};
`;